import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast, Toaster } from "sonner";
import { PageHeader } from "../components/PageHeader";
import TurnstileWidget from "../components/TurnstileWidget";
import { checkSiret } from "../lib/siret";
import { useDocumentMeta } from "../hooks/use-document-meta";
import settings from "../content/settings.json";
import contactContent from "../content/pages/contact.json";

const SETTINGS_CONTACT = settings.contact || {};
const SALES_EMAIL = SETTINGS_CONTACT.email || "contact@fritesdorees.fr";
const PHONE_DISPLAY = SETTINGS_CONTACT.phone_display || "01 48 68 03 03";
const PHONE_LINK = SETTINGS_CONTACT.phone_link || "+33148680303";
const PHONE_FULL = `+33 ${PHONE_DISPLAY.replace(/^0/, "")}`;

const ADDRESS = SETTINGS_CONTACT.address || {};
const ADDRESS_LINES = [
  ADDRESS.street,
  [ADDRESS.postal_code, ADDRESS.city].filter(Boolean).join(" ") + (ADDRESS.country ? `, ${ADDRESS.country}` : ""),
].filter(Boolean);

const HOURS = (SETTINGS_CONTACT.hours || []).map((h) => {
  // Accept either {days, range} (current) or {days, open, close} (legacy).
  const range = h.range || [h.open, h.close].filter(Boolean).join(" - ");
  return [h.days, range].filter(Boolean).join(" · ");
});

const ACTIVITY_TYPES = contactContent.activity_types || [];
const INTEREST_LABELS = contactContent.interest_labels || {};
const META = contactContent.meta || {};
const FORM_LABELS = (contactContent.form && contactContent.form.labels) || {};
const VALIDATION_ERROR = (contactContent.form && contactContent.form.validation_error) || "Merci de remplir les champs obligatoires.";
const BIG_PHONE_CTA = contactContent.big_phone_cta || {};
const SUCCESS_PANEL = contactContent.success_panel || {};
const ERROR_PANEL = contactContent.error_panel || {};
const INFO_PANEL = contactContent.info_panel || {};
const ROW_LABELS = INFO_PANEL.row_labels || {};
const GARANTIE_24H = contactContent.garantie_24h || {};
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function Contact() {
  useDocumentMeta({
    title: "Contact — Devis B2B sous 24h · Grossiste fast-food",
    description:
      "Demande de devis B2B et de catalogue grossiste fast-food sous 24h. Frites Dorées, Aulnay-sous-Bois (93) — fournisseur frites surgelées, sauces et emballages pro. Téléphone : 01 48 68 03 03. Près de 300 références.",
    canonicalPath: "/contact",
  });
  const [searchParams] = useSearchParams();
  const initialProduct = searchParams.get("produit") || "";
  const initialInterest = searchParams.get("interest") || "";
  const initialRequest = searchParams.get("request") || "";
  const interestLabel = INTEREST_LABELS[initialInterest] || "";

  const buildInitialMessage = () => {
    if (initialProduct) {
      // `request=fiche` (Product page secondary CTA) → ask for the spec sheet.
      if (initialRequest === "fiche") {
        return `Bonjour, je souhaite recevoir la fiche technique du produit "${initialProduct}".`;
      }
      return `Bonjour, je souhaite plus d'informations sur le produit "${initialProduct}".`;
    }
    if (interestLabel) {
      return `Bonjour, je souhaite recevoir une sélection et un tarif sur la gamme « ${interestLabel} ».`;
    }
    // Footer/navbar/home "Demander le catalogue" CTAs land here. The
    // pre-filled message keeps lead intent explicit for the sales team and
    // saves the prospect from writing anything to fire the request.
    // Wording lead-first : pas de mention de PDF public — le catalogue est
    // envoyé par un commercial après qualification.
    if (initialRequest === "catalogue") {
      return "Bonjour, je souhaite recevoir le catalogue B2B Frites Dorées (tarifs grossiste, disponibilités, conditions de livraison) via un commercial.";
    }
    return "";
  };

  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    company: "", siret: "",
    activity_type: "",
    // Optional B2B qualification fields — pre-qualify leads so the sales
    // team arrives prepped. All optional so we don't add friction.
    sites: "",
    volume: "",
    zone: "",
    message: buildInitialMessage(),
    website: "", // honeypot — must stay empty
  });
  // Form state machine:
  //  'idle'    — form visible, awaiting submit
  //  'sending' — POST in flight, button disabled
  //  'success' — backend confirmed delivery, success panel shown
  //  'error'   — backend rejected / network failed, error panel with mailto + tel fallback
  const [status, setStatus] = useState("idle");
  const [consent, setConsent] = useState(false);
  const [siretError, setSiretError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [ref, setRef] = useState("");
  // Instant d'affichage du formulaire : sert de délai minimal anti-robot côté serveur.
  const mountedAt = useRef(Date.now());

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const buildSubject = () => {
    if (initialProduct) return `Demande produit : ${initialProduct}`;
    if (interestLabel) return `Devis gamme : ${interestLabel}`;
    if (initialRequest === "catalogue") return form.activity_type ? `Demande de catalogue · ${form.activity_type}` : "Demande de catalogue";
    if (form.activity_type) return `Demande de devis · ${form.activity_type}`;
    return "Demande de devis B2B";
  };

  const buildBody = () => {
    const lines = [
      "Bonjour,",
      "",
      "Je souhaite recevoir un devis personnalisé.",
      "",
      "— Informations contact —",
      `Nom : ${form.name}`,
      `Société : ${form.company}`,
      `Email : ${form.email}`,
    ];
    if (form.siret) lines.push(`SIRET : ${form.siret}`);
    if (form.phone) lines.push(`Téléphone : ${form.phone}`);
    if (form.activity_type) lines.push(`Type d'activité : ${form.activity_type}`);
    if (form.sites) lines.push(`Nombre de sites : ${form.sites}`);
    if (form.volume) lines.push(`Volume mensuel estimé : ${form.volume}`);
    if (form.zone) lines.push(`Zone de livraison : ${form.zone}`);
    if (initialProduct) {
      lines.push("", "— Produit ciblé —", initialProduct);
    } else if (interestLabel) {
      lines.push("", "— Gamme d'intérêt —", interestLabel);
    }
    lines.push("", "— Message —", form.message);
    if (typeof window !== "undefined") {
      lines.push("", `— Page source —`, window.location.href);
    }
    lines.push("", "Merci pour votre retour.");
    return lines.join("\n");
  };

  // Best-effort mailto fallback URL — reused by the error panel so a prospect
  // can recover from a backend failure with one click.
  const fallbackMailto = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(buildSubject())}&body=${encodeURIComponent(buildBody())}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company.trim() || !form.message) {
      toast.error(VALIDATION_ERROR);
      return;
    }
    if (!checkSiret(form.siret).valid) {
      setSiretError(true);
      toast.error(FORM_LABELS.siret_error || "Le SIRET semble incorrect (14 chiffres). Corrigez-le ou laissez le champ vide.");
      return;
    }
    if (!consent) {
      toast.error(FORM_LABELS.consent_error || "Merci d'accepter le traitement de vos données pour envoyer la demande.");
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error(FORM_LABELS.captcha_error || "Merci de valider la vérification anti-robot.");
      return;
    }
    // Honeypot: silently swallow if the hidden field was filled.
    if (form.website) {
      setStatus("success");
      return;
    }

    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: form.company,
          siret: form.siret,
          consent: true,
          request: initialRequest,
          elapsed_ms: Date.now() - mountedAt.current,
          turnstile_token: turnstileToken,
          activity_type: form.activity_type,
          sites: form.sites,
          volume: form.volume,
          zone: form.zone,
          message: form.message,
          product: initialProduct,
          interest: interestLabel,
          website: form.website, // honeypot pass-through for backend drop
          page_source: typeof window !== "undefined" ? window.location.href : "",
        }),
      });
      if (!res.ok) throw new Error(`backend_${res.status}`);
      // We require an explicit `ok: true` payload. A 200 carrying the SPA
      // index.html (case where /api/contact isn't deployed yet) parses as
      // empty {} or throws — both end up in `error` state. No false success.
      const payload = await res.json().catch(() => null);
      if (!payload || payload.ok !== true) throw new Error(payload?.error || "no_ok_payload");
      setRef(typeof payload.ref === "string" ? payload.ref : "");
      setTurnstileToken("");
      setStatus("success");
    } catch (err) {
      // Backend unreachable (local dev, DNS not set up yet, Worker down,
      // whatever) — fail loudly. Never show false success.
      // eslint-disable-next-line no-console
      console.error("contact submit failed", err);
      setTurnstileToken("");
      setTurnstileReset((n) => n + 1);
      setStatus("error");
    }
  };

  return (
    <main data-testid="contact-page" className="bg-[#050505]">
      <Toaster theme="dark" position="top-center" />
      <PageHeader
        kicker={META.kicker}
        title={
          <>
            {META.title}
            {META.title_highlight && (
              <>
                {" "}
                <br />
                <span className="text-[#FFC300]">{META.title_highlight}</span>
              </>
            )}
          </>
        }
        subtitle={META.subtitle}
      />

      {/* Big phone CTA strip */}
      <section className="border-b border-white/5 py-10 px-6 md:px-10 lg:px-14">
        <a href={`tel:${PHONE_LINK}`} className="group flex flex-col md:flex-row md:items-center md:justify-between gap-4 hover:bg-[#FFC300]/5 p-6 lg:p-8 transition-colors -mx-6 md:-mx-10 lg:-mx-14 px-6 md:px-10 lg:px-14" data-testid="big-phone-cta">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-[#FFC300] text-black flex items-center justify-center shrink-0">
              <Phone size={24} />
            </div>
            <div>
              <p className="tag-label mb-1">{BIG_PHONE_CTA.kicker || "Appeler directement · Plus rapide"}</p>
              <p className="font-display text-3xl md:text-5xl uppercase tracking-tighter group-hover:text-[#FFC300] transition-colors">{PHONE_DISPLAY}</p>
            </div>
          </div>
          <ArrowUpRight size={28} className="text-[#FFC300] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </a>
      </section>

      <section className="px-6 md:px-10 lg:px-14 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="tag-label mb-5 flex items-center gap-3">
              <span className="w-10 h-px bg-[#FFC300]"></span>
              <span>{(contactContent.form && contactContent.form.section_kicker) || "Entrer en contact"}</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95] mb-8">
              {(contactContent.form && contactContent.form.section_title) || "Demande de devis B2B"}
            </h2>

            {status === "success" ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-[#FFC300]/30 bg-[#FFC300]/5 p-10"
                data-testid="contact-success"
              >
                <Check size={32} className="text-[#FFC300] mb-4" />
                <h3 className="font-display text-3xl uppercase tracking-tight mb-3">{SUCCESS_PANEL.title || "Votre demande est partie."}</h3>
                <p className="font-body text-zinc-300 max-w-md leading-relaxed">
                  {SUCCESS_PANEL.body || "Un expert Frites Dorées la reçoit en direct. Réponse sous 24h ouvrées avec un devis chiffré et une proposition produits ciblée."}
                </p>
                {ref && (
                  <p className="mt-4 font-body text-zinc-300 text-sm" data-testid="contact-ref">
                    {SUCCESS_PANEL.ref_label || "Votre référence :"} <strong className="text-[#FFC300] tracking-wide">{ref}</strong>
                    {" "}— {SUCCESS_PANEL.ref_hint || "à rappeler si vous nous contactez."}
                  </p>
                )}
                <p className="mt-5 font-body text-zinc-400 text-sm">
                  {SUCCESS_PANEL.fallback_prefix || "Une copie a aussi été préparée pour"}{" "}
                  <a href={`mailto:${SALES_EMAIL}`} className="text-[#FFC300] underline">{SALES_EMAIL}</a>{" "}
                  {SUCCESS_PANEL.fallback_or || "ou appelez le"}{" "}
                  <a href={`tel:${PHONE_LINK}`} className="text-[#FFC300] underline">{PHONE_DISPLAY}</a>.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 tag-label hover:text-[#FFC300] transition-colors"
                  data-testid="contact-reset"
                >
                  {SUCCESS_PANEL.reset_label || "← Faire une autre demande"}
                </button>
              </motion.div>
            ) : status === "error" ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-red-500/40 bg-red-500/[0.04] p-10"
                data-testid="contact-error"
              >
                <h3 className="font-display text-3xl uppercase tracking-tight mb-3 text-red-300">
                  {ERROR_PANEL.title || "L'envoi n'a pas abouti."}
                </h3>
                <p className="font-body text-zinc-300 max-w-md leading-relaxed">
                  {ERROR_PANEL.body || "Nous n'avons pas pu transmettre votre demande dans l'immédiat. Vous pouvez nous joindre directement :"}
                </p>
                <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
                  <a
                    href={fallbackMailto}
                    className="btn-primary inline-flex items-center justify-center gap-2"
                    data-testid="contact-error-mailto"
                  >
                    {ERROR_PANEL.mailto_label || "Écrire à"} {SALES_EMAIL}
                  </a>
                  <a
                    href={`tel:${PHONE_LINK}`}
                    className="btn-secondary inline-flex items-center justify-center gap-2"
                    data-testid="contact-error-tel"
                  >
                    <Phone size={14} /> {PHONE_DISPLAY}
                  </a>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="btn-tertiary"
                    data-testid="contact-error-retry"
                  >
                    {ERROR_PANEL.retry_label || "Réessayer"}
                  </button>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
                {/* Honeypot — invisible to humans, filled by naive bots. Backend silently drops if non-empty. */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={onChange}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", top: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                <Field label={FORM_LABELS.name || "Nom *"}><input data-testid="contact-name" name="name" value={form.name} onChange={onChange} required autoComplete="name" /></Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={FORM_LABELS.company || "Société *"}><input data-testid="contact-company" name="company" value={form.company} onChange={onChange} required autoComplete="organization" /></Field>
                  <Field label={FORM_LABELS.siret || "SIRET (facultatif)"}>
                    <input
                      data-testid="contact-siret"
                      name="siret"
                      value={form.siret}
                      onChange={(e) => { setSiretError(false); onChange(e); }}
                      onBlur={() => setSiretError(!checkSiret(form.siret).valid)}
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder={FORM_LABELS.siret_placeholder || "14 chiffres"}
                      aria-invalid={siretError}
                    />
                    {siretError && (
                      <span className="mt-1 block font-body text-xs text-red-300" data-testid="contact-siret-error">
                        {FORM_LABELS.siret_error || "Le SIRET semble incorrect (14 chiffres). Corrigez-le ou laissez le champ vide."}
                      </span>
                    )}
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={FORM_LABELS.email || "Email *"}><input data-testid="contact-email" name="email" type="email" value={form.email} onChange={onChange} required /></Field>
                  <Field label={FORM_LABELS.phone || "Téléphone"}><input data-testid="contact-phone" name="phone" value={form.phone} onChange={onChange} /></Field>
                </div>
                <Field label={FORM_LABELS.activity || "Type d'activité"}>
                  <select data-testid="contact-activity" name="activity_type" value={form.activity_type} onChange={onChange}>
                    <option value="">{FORM_LABELS.activity_placeholder || "Sélectionner..."}</option>
                    {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                {/* Optional B2B qualification fields — help the sales team
                    triage incoming demands without adding required friction. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={FORM_LABELS.sites || "Nombre de sites"}>
                    <select data-testid="contact-sites" name="sites" value={form.sites} onChange={onChange}>
                      <option value="">{FORM_LABELS.sites_placeholder || "Optionnel..."}</option>
                      <option value="1 site">1 site</option>
                      <option value="2 à 5 sites">2 à 5 sites</option>
                      <option value="6 à 20 sites">6 à 20 sites</option>
                      <option value="Plus de 20 sites">Plus de 20 sites</option>
                    </select>
                  </Field>
                  <Field label={FORM_LABELS.volume || "Volume mensuel estimé"}>
                    <select data-testid="contact-volume" name="volume" value={form.volume} onChange={onChange}>
                      <option value="">{FORM_LABELS.volume_placeholder || "Optionnel..."}</option>
                      <option value="Moins de 500 €">Moins de 500 €</option>
                      <option value="500 – 2 000 €">500 – 2 000 €</option>
                      <option value="2 000 – 10 000 €">2 000 – 10 000 €</option>
                      <option value="Plus de 10 000 €">Plus de 10 000 €</option>
                      <option value="À définir">À définir</option>
                    </select>
                  </Field>
                </div>
                <Field label={FORM_LABELS.zone || "Zone de livraison (CP ou département)"}>
                  <input data-testid="contact-zone" name="zone" value={form.zone} onChange={onChange} placeholder={FORM_LABELS.zone_placeholder || "Ex. 93600 ou Lyon (69)"} />
                </Field>
                <Field label={FORM_LABELS.message || "Message *"}>
                  <textarea
                    data-testid="contact-message"
                    name="message"
                    rows="5"
                    value={form.message}
                    onChange={onChange}
                    required
                    placeholder={FORM_LABELS.message_placeholder || "Volume mensuel estimé, fréquence de commande, produits ciblés..."}
                  />
                </Field>

                <label className="flex items-start gap-3 cursor-pointer" data-testid="contact-consent-label">
                  <input
                    type="checkbox"
                    data-testid="contact-consent"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required
                    className="mt-1 h-4 w-4 shrink-0 accent-[#FFC300]"
                  />
                  <span className="font-body text-xs leading-relaxed text-zinc-400">
                    {FORM_LABELS.consent_prefix || "J'accepte que Frites Dorées utilise ces informations pour traiter ma demande, conformément à la"}{" "}
                    <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="text-[#FFC300] underline" data-testid="contact-privacy-link">
                      {FORM_LABELS.consent_link || "politique de confidentialité"}
                    </a>. *
                  </span>
                </label>

                {TURNSTILE_SITE_KEY && (
                  <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} resetSignal={turnstileReset} />
                )}

                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="btn-primary w-full justify-center mt-4 disabled:opacity-60 disabled:cursor-wait"
                  disabled={status === "sending"}
                >
                  {status === "sending"
                    ? (FORM_LABELS.submit_sending || "Envoi en cours...")
                    : (FORM_LABELS.submit || "Recevoir mon devis sous 24h")}
                  {status !== "sending" && <ArrowUpRight size={18} />}
                </button>
                <p className="mt-3 text-center font-body text-xs text-zinc-500">
                  {FORM_LABELS.submit_hint || `Votre demande est envoyée directement à notre équipe commerciale. Réponse sous 24h ouvrées.`}
                </p>
              </form>
            )}
          </div>

          {/* Infos */}
          <div className="lg:col-span-5 lg:pl-10 lg:border-l border-white/10">
            <div className="tag-label mb-5 flex items-center gap-3">
              <span className="w-10 h-px bg-[#FFC300]"></span>
              <span>{INFO_PANEL.kicker || "Coordonnées"}</span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tighter leading-[0.95] mb-10">{INFO_PANEL.title || "Frites Dorées — Aulnay-sous-Bois"}</h3>

            <ul className="space-y-8">
              <InfoRow icon={MapPin} title={ROW_LABELS.address || "Adresse"} lines={ADDRESS_LINES} />
              <InfoRow icon={Phone} title={ROW_LABELS.phone || "Téléphone"} lines={[<a key="p" href={`tel:${PHONE_LINK}`} className="hover:text-[#FFC300] transition-colors">{PHONE_FULL}</a>]} />
              <InfoRow icon={Mail} title={ROW_LABELS.email || "Email"} lines={[<a key="e" href={`mailto:${SALES_EMAIL}`} className="hover:text-[#FFC300] transition-colors">{SALES_EMAIL}</a>]} />
              <InfoRow icon={Clock} title={ROW_LABELS.hours || "Horaires"} lines={HOURS} />
            </ul>

            <div className="mt-12 border border-white/10 p-6 bg-[#0d0d0d]">
              <p className="tag-label text-[#FFC300] mb-3">{GARANTIE_24H.kicker || "Garantie 24h"}</p>
              <p className="font-body text-zinc-300 leading-relaxed text-sm">{GARANTIE_24H.body || "Toute demande reçue avant 17h reçoit une réponse sous 24h ouvrées, avec un devis chiffré et une proposition produits ciblée."}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="px-6 md:px-10 lg:px-14 pb-20">
        <div className="relative h-[400px] overflow-hidden border border-white/10">
          <iframe
            data-testid="contact-map"
            title="Carte Frites Dorées"
            src="https://www.openstreetmap.org/export/embed.html?bbox=2.4930%2C48.9450%2C2.5180%2C48.9600&layer=mapnik&marker=48.9525%2C2.5055"
            className="w-full h-full grayscale contrast-125 invert-[0.92] hue-rotate-180"
            style={{ border: 0 }}
            loading="lazy"
          />
        </div>
      </section>
    </main>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="tag-label !text-[10px] mb-2 block">{label}</span>
    {children}
  </label>
);

const InfoRow = ({ icon: Icon, title, lines }) => (
  <li className="flex gap-4">
    <div className="w-10 h-10 border border-white/15 flex items-center justify-center shrink-0">
      <Icon size={16} className="text-[#FFC300]" />
    </div>
    <div>
      <p className="tag-label mb-2">{title}</p>
      {lines.map((l, i) => <p key={i} className="font-body text-zinc-200">{l}</p>)}
    </div>
  </li>
);
